/**
 * Thread.js — chat thread screen.
 *
 * Reactive: subscribes to $threads.<openPhone> nanostore. New messages
 * (optimistic, realtime, status updates) flow through the store and the
 * UI auto-renders. No manual scroll-to-bottom hacks for state sync.
 */

import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '@nanostores/preact';

import { $currentMessages } from '../stores/messages.js';
import {
  $openChatPhone, $openChatId, $openChatChannel,
  closeThread, showToast,
  $sheets, openSheet,
  $ephemeralHandoffNote, setEphemeralHandoffNote, clearEphemeralHandoffNote,
} from '../stores/ui.js';
import { $currentChat, updateChat, updateChatByContactId } from '../stores/chats.js';
import { getChannelColor, $channelMeta } from '../services/channel-colors.js';
import { $isOnline } from '../services/online-status.js';
import { getSession } from '../stores/session.js';
import { resolveAgent, $team } from '../services/team-directory.js';
import { DIRECTION } from '../shared/constants.js';
import { sessionKind, SESSION_WINDOW_HOURS as _SHARED_SESSION_HOURS } from '../shared/session-window.js';
import {
  loadMessages,
  loadOlderMessages,
  getPaginationState,
  sendText,
  sendReaction,
  markUnreadAsRead,
} from '../services/messages-service.js';
import * as api from '../services/api.js';

import { MessageBubble } from './MessageBubble.js';
import { Composer } from './Composer.js';
import { ContactPanel } from './ContactPanel.js';
import { InteractivePicker } from './InteractivePicker.js';
import { SessionCountdown } from './SessionCountdown.js';
import { $scheduled, loadScheduled, addScheduled, removeScheduled, clearScheduled } from '../stores/scheduled.js';
import { TeamMemberPicker }       from './TeamMemberPicker.js';
import { TransferConfirmModal }   from './TransferConfirmModal.js';
import { NotesSheet }             from './NotesSheet.js';
import { loadNotes }              from '../services/lead-notes.js';

/**
 * Convert a timestamp (seconds OR milliseconds) to a Date object in the
 * browser's local timezone. WhatsApp Cloud API timestamps are seconds;
 * inbox_messages.timestamp is also seconds. Anything > 1e10 is assumed ms.
 */
function _toDate(ts) {
  if (!ts) return null;
  const ms = ts > 1e10 ? ts : ts * 1000;
  const d = new Date(ms);
  return isNaN(d) ? null : d;
}

/** Same calendar day in the user's local timezone? */
function _sameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth() &&
         a.getDate()     === b.getDate();
}

/**
 * Build a one-line preview for a message, used in the reply quote bubble.
 * Mirrors the webhook's buildPreview but computed client-side from the
 * normalized message shape (text.body, image.caption, etc).
 */
function _quotePreview(target) {
  if (!target) return '';
  if (target.text?.body)        return target.text.body;
  if (target.image?.caption)    return '📷 ' + target.image.caption;
  if (target.image)             return '📷 Photo';
  if (target.video?.caption)    return '🎬 ' + target.video.caption;
  if (target.video)             return '🎬 Video';
  if (target.audio || target.voice) return '🔊 Audio';
  if (target.document?.filename) return '📄 ' + target.document.filename;
  if (target.document)          return '📄 Document';
  if (target.location)          return '📍 ' + (target.location.name || 'Location');
  // #156: interactive messages don't have text.body at the top level — the
  // human-readable question is inside interactive.body.text (body) or
  // interactive.header.text (header). Fall back through these in priority
  // order before emitting the generic "[interactive]" label. For inbound
  // replies (button_reply / list_reply) show the tapped title so the quote
  // reads "You · Yes" instead of "You · [interactive]". No icon prefix —
  // the quote already labels who replied via the quoteLabel ("You" /
  // "Customer"), so a second glyph here just clutters the small quote bar.
  if (target.interactive) {
    const it = target.interactive;
    if (it.body?.text)                   return it.body.text;
    if (it.header?.type === 'text' && it.header.text) return it.header.text;
    if (it.button_reply?.title)          return it.button_reply.title;
    if (it.list_reply?.title)            return it.list_reply.title;
    if (it.nfm_reply?.name)              return it.nfm_reply.name;
    return 'Interactive';
  }
  if (target.type)              return `[${target.type}]`;
  return '[Message]';
}

/** "Today" / "Yesterday" / "Apr 11" / "Apr 11, 2024" — uses browser locale + tz. */
function _formatDayLabel(ts) {
  const d = _toDate(ts);
  if (!d) return '';
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);

  if (_sameDay(d, today)) return 'Today';
  if (_sameDay(d, yest))  return 'Yesterday';

  // Within current year → omit year; otherwise include it.
  if (d.getFullYear() === today.getFullYear()) {
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * #160 — assign on intent, not on open.
 *
 * Replaces the old auto-assign-on-mount behavior. Reading a chat is now
 * a first-class read-only action — opening someone else's conversation
 * never steals ownership from them. Assignment only happens when the
 * agent engages: sends a text, sends media, reacts to a message, or
 * replies to one.
 *
 * Returns one of three decisions synchronously:
 *   - 'proceed'  — chat is unassigned (silent self-claim) OR already
 *                  assigned to the current user. Action can run
 *                  immediately. For the unassigned case the caller
 *                  fires the assign API as a side effect.
 *   - 'confirm'  — chat is assigned to a different agent. Caller must
 *                  show the transfer-confirmation modal and wait for
 *                  the user's decision before proceeding.
 *   - 'deny'     — session is missing or misconfigured; block the action.
 *
 * The decision result also carries the resolved current assignee so
 * the modal can render "Assigned to {name}" without a second lookup.
 */
function _assignmentDecision() {
  const session = getSession();
  if (!session?.agentEmail) return { kind: 'deny', reason: 'no session' };

  const liveChat = $currentChat.get();
  if (!liveChat) return { kind: 'deny', reason: 'no chat' };

  // Unassigned → first engagement silently claims it.
  if (!liveChat.agent) {
    return { kind: 'proceed', assignedToSelf: false, willClaim: true };
  }

  // Resolve current assignee to check if it's the logged-in user. The
  // resolver returns isYou=true when chat.agent maps to session.user_email.
  const current = resolveAgent(liveChat.agent);
  if (current.isYou) {
    return { kind: 'proceed', assignedToSelf: true, willClaim: false };
  }

  // Someone else owns this chat — caller must confirm transfer.
  return { kind: 'confirm', currentAssignee: current };
}

/**
 * Silent self-claim for the "unassigned → first engagement" path.
 * Fires the assign API optimistically + patches the store so the chat
 * row badge updates immediately. No modal, no toast — the act of
 * sending already implies intent.
 *
 * #163: scoped to the specific (contact, channel) row. Previously
 * used updateChatByContactId which applied the patch to every channel
 * of the contact; that's wrong now that rows are independent.
 */
function _claimForSelf(chatId, phone, channelPhoneId) {
  const session = getSession();
  if (!session?.agentEmail) return;
  const liveChat = $currentChat.get();
  if (!liveChat) return;

  console.log('[v2 thread] claiming chat on intent:', session.agentEmail);
  if (liveChat.id) updateChat(liveChat.id, { agent: session.agentEmail, isDone: false });
  // Pass display name + previous email so server-side label/audit are
  // accurate. customerName lets the receiving agent's notification copy
  // include the contact ("Sambit assigned you Sambit Cloud" — #172).
  api.assignChat(chatId, phone, session.agentEmail, channelPhoneId, {
    agentName:           session.agentName || session.agentEmail,
    previousAgentEmail:  liveChat.agent || null,
    customerName:        liveChat.name || null,
  }).catch(e => {
    console.warn('[v2 thread] claim API failed:', e.message);
  });
}

/**
 * Execute an explicit transfer from the current owner to the logged-in
 * user. Used by the transfer-confirmation modal after the agent taps
 * "Transfer". #163: per-row scope via channelPhoneId.
 */
function _transferToSelf(chatId, phone, channelPhoneId) {
  const session = getSession();
  if (!session?.agentEmail) return Promise.reject(new Error('no session'));
  const live = $currentChat.get();
  console.log('[v2 thread] transferring chat to self:', session.agentEmail);
  if (live?.id) updateChat(live.id, { agent: session.agentEmail, isDone: false });
  return api.assignChat(chatId, phone, session.agentEmail, channelPhoneId, {
    agentName:           session.agentName || session.agentEmail,
    previousAgentEmail:  live?.agent || null,
    customerName:        live?.name || null,
  });
}

/**
 * Is the scroll container within STICK_PX of its natural bottom? Used to
 * decide whether an incoming message should yank the view or show a "↓ N
 * new" pill instead. Defaults true when called on an unmounted ref so the
 * very first render stays pinned to bottom.
 */
const STICK_PX = 120;
/**
 * Format a stored E.164 phone for the thread header. Mirrors the proxy-side
 * helper in inbox-webhook.js (#176) so the in-app header reads the same way
 * as the lock-screen banner. Indian +91 numbers get the "+91 9XXXX XXXXX"
 * grouping; everything else falls back to "+CC <4-digit groups>".
 */
function _formatHeaderPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 7) return phone || '';
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  const cc = digits.slice(0, digits.length - 10) || digits.slice(0, 1);
  const rest = digits.slice(cc.length).match(/.{1,4}/g)?.join(' ') || '';
  return `+${cc} ${rest}`.trim();
}

function _isNearBottom(el) {
  if (!el) return true;
  const slack = el.scrollHeight - el.clientHeight - el.scrollTop;
  return slack < STICK_PX;
}

/**
 * 24-hour send-window check. The WA Cloud API only lets the business send
 * freeform (non-template) messages within 24 hours of the customer's most
 * recent inbound message. After that, Meta rejects with error 131047 and
 * the agent must wait for the customer to message again.
 *
 * Returns null when the window is open OR when lastInboundTs is unknown.
 * The "unknown → don't block" branch is intentional: chats imported from
 * other channels (e.g. Prinstaindia) may have CRM history but no rows in
 * our inbox_messages table because our webhook didn't cover that channel.
 * Locking those would be a false positive. When expired, returns
 * { expired: true, sinceHours, label } so the UI can render a banner.
 */
const SESSION_WINDOW_HOURS = _SHARED_SESSION_HOURS;
/**
 * T2 — map machine-readable scheduled failure codes to human copy that
 * fits in the ghost bubble's status line. Anything unknown falls back
 * to the first word of the raw reason so we still surface *something*
 * beyond a generic "Failed" (SR users need the cause to decide whether
 * to retry vs. edit the message).
 */
function _shortReason(raw) {
  if (!raw) return '';
  const map = {
    session_expired:      'Session expired',
    no_inbound_history:   'No customer reply yet',
    'CRM API 400':        'Meta rejected',
    'CRM API 401':        'Auth expired',
    'CRM API 403':        'Not permitted',
    'No channel token available': 'Token unavailable',
  };
  for (const k of Object.keys(map)) if (raw.startsWith(k)) return map[k];
  return raw.split(/[\s:—-]/)[0].slice(0, 24);
}

// Format the lead-source value from CRM custom fields. Known codes get
// hand-tuned display; anything unknown falls through to humanized
// snake_case so a brand-new value at least reads cleanly. This is the
// ONLY string we surface in the campaign-echo card — we deliberately
// do NOT display utm_term or any inferred template name, because we
// don't actually have the template payload (Partner Panel campaign
// sends don't echo to our webhook). Showing a fabricated template
// title misled agents — Karthikeyan didn't receive APEX FORGE 2.0
// just because his lead is attributed to that broadcast.
const LEAD_SOURCE_DISPLAY = {
  wa_keyword:  'WhatsApp keyword',
  wa_referral: 'WhatsApp referral',
  sticky_bar:  'Sticky bar',
  qr_code:     'QR code',
  facebook_ad: 'Facebook ad',
  google_ad:   'Google ad',
};
function _formatLeadSource(src) {
  if (!src) return '';
  const k = String(src).toLowerCase();
  if (LEAD_SOURCE_DISPLAY[k]) return LEAD_SOURCE_DISPLAY[k];
  return String(src)
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, m => m.toUpperCase())
    .trim();
}

function _sessionWindowStatus(lastInboundTs) {
  if (!lastInboundTs) return null; // unknown — don't block
  const nowSec = Math.floor(Date.now() / 1000);
  const ageSec = nowSec - lastInboundTs;
  if (ageSec < SESSION_WINDOW_HOURS * 3600) return null;
  const ageH = ageSec / 3600;
  let label;
  if (ageH < 48)                   label = 'more than a day ago';
  else if (ageH < 24 * 7)          label = `${Math.floor(ageH / 24)} days ago`;
  else if (ageH < 24 * 30)         label = `${Math.floor(ageH / (24 * 7))} weeks ago`;
  else                             label = 'over a month ago';
  return { expired: true, sinceHours: ageH, label };
}

export function Thread() {
  const phone    = useStore($openChatPhone);
  const chatId   = useStore($openChatId);
  const channelPhoneId = useStore($openChatChannel); // active channel for accent
  const messages = useStore($currentMessages); // re-renders only when OPEN thread changes
  const chat     = useStore($currentChat);     // re-renders only when OPEN chat changes
  const sheets   = useStore($sheets);           // drives bottom-sheet visibility
  const allScheduled = useStore($scheduled);
  const schedKey = (phone && channelPhoneId) ? `${phone}:${channelPhoneId}` : phone;
  const scheduledList = (schedKey && allScheduled[schedKey]) || [];
  // Subscribe so dashboard color edits re-render the header accent live
  useStore($channelMeta);
  // Channel color for the header accent line — same hint visual language
  // as the chat row's left edge stripe.
  const channelColor = channelPhoneId ? getChannelColor(channelPhoneId) : null;

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  // "N new messages" pill count — incremented each time a new bubble arrives
  // while the user is scrolled up reading history. Reset to 0 whenever the
  // user returns to the bottom (either by scrolling or by tapping the pill).
  const [unreadBelow, setUnreadBelow] = useState(0);
  // Load-older spinner visibility — shown while a pagination fetch is in
  // flight, and hidden permanently once we've seen the oldest message.
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [pagDone, setPagDone] = useState(false);
  // T8 — Send-now signal to Composer. Shape: { text, nonce }. Changing
  // nonce triggers the Composer's prefill effect. Default null so the
  // effect no-ops on mount; we only set this on Send-now tap.
  const [prefillSignal, setPrefillSignal] = useState(null);

  const containerRef = useRef(null);
  // T13 — user-intent sticky-to-bottom flag (replaces the old
  // wall-clock initialLoadLockUntilRef). Starts true on thread mount;
  // the ResizeObserver re-snaps to bottom on every container resize
  // while this is true (image bubbles loading bytes 30s later, late
  // reactions, etc). Flips to false the moment the user scrolls up
  // away from the bottom, so we never fight an intentional scroll.
  // Flips back to true when the user returns near the bottom or taps
  // the "scroll to bottom" fab. No wall-clock expiry — async media
  // loading beyond any fixed window still keeps the viewport at the
  // latest message.
  const stickToBottomRef = useRef(true);
  // Snapshot captured BEFORE each render so the post-render effect knows
  // whether the user was near the bottom when the new message arrived.
  // Refs (not state) because we must read this synchronously during render,
  // and mutating it must never trigger a re-render cascade.
  const wasNearBottomRef = useRef(true);
  const prevMsgCountRef  = useRef(0);
  // Anchor scroll position captured just before a pagination fetch resolves,
  // so the layout effect can restore it after older bubbles are prepended.
  // Value: scrollHeight - scrollTop (distance from the bottom), an invariant
  // under prepending — restoring it keeps the same visual content in view.
  const pagAnchorRef = useRef(null);

  // Snapshot the scroll position BEFORE the DOM commits the new bubble —
  // once the bubble lands, scrollHeight grows and _isNearBottom is no longer
  // meaningful for the pre-append state.
  wasNearBottomRef.current = _isNearBottom(containerRef.current);

  // Load messages when thread opens
  useEffect(() => {
    if (!chatId || !phone) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUnreadBelow(0);
    setLoadingOlder(false);
    setPagDone(false);
    prevMsgCountRef.current = 0;
    // T13 — fresh thread switch: re-enable stickiness so the initial
    // snap below + any async media loads after it anchor to bottom.
    stickToBottomRef.current = true;
    // #153: pass the row's channelPhoneId so messages are scoped to this
    // single (contact, channel) thread. Without this, opening Sambit's
    // Prinstaindia row would show messages from the ODIV channel too.
    //
    // #160: NO auto-assign on open. Reading a chat is read-only by
    // default — ownership only changes when the agent actually sends
    // a message / reacts / replies. See _assignmentDecision below.
    loadScheduled(phone, channelPhoneId).catch(() => {});
    loadMessages(chatId, phone, channelPhoneId)
      .then(() => {
        if (cancelled) return;
        setLoading(false);
        // Force scroll to bottom after the container mounts. setLoading(false)
        // triggers a render that adds the container to the DOM — setTimeout 0
        // fires after that render commits + paints.
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
        }, 50);
        markUnreadAsRead(phone, channelPhoneId).catch(e =>
          console.warn('[v2 thread] markUnreadAsRead failed:', e.message)
        );
        // T10 — 1s deferred belt-and-braces. Re-snaps to bottom once
        // slow media bubbles are likely to have finished loading, and
        // re-fires markUnreadAsRead in case the initial call above
        // raced with a realtime insert that arrived mid-flight (the
        // new row lands `delivered`, first mark-read already returned,
        // so chat row stays at unread=1). Also covers silent-fail on
        // the first call — the function self-throttles via status
        // filters, so the retry is a no-op on the happy path.
        setTimeout(() => {
          if (cancelled) return;
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
          markUnreadAsRead(phone, channelPhoneId).catch(() => {});
        }, 1000);
      })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [chatId, phone, channelPhoneId]);

  // T10 — Visibility-change snap. Covers the PWA lifecycle: agent
  // backgrounds the app → messages arrive via FCM/realtime → agent
  // foregrounds → Thread doesn't remount (it was already mounted), so
  // the mount-time scroll effect above doesn't fire. Without this
  // handler, new inbound rows append below the viewport and the agent
  // lands on whatever they were last looking at — typically "slightly
  // older messages" from where they left off.
  //
  // We only snap when the tab is coming BACK to foreground (!hidden)
  // and only when the thread has actual content. No throttle: browsers
  // fire visibilitychange once per transition.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) return;
      if (!containerRef.current) return;
      if ((messages?.length || 0) === 0) return;
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      // Also re-fire mark-read — messages that arrived while backgrounded
      // are now visible; clear the delivered flag on them too.
      if (phone) markUnreadAsRead(phone, channelPhoneId).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [phone, channelPhoneId, messages?.length]);

  // Pagination trigger: when the user scrolls near the top of the container,
  // fetch the next older page. Runs through the scroll event handler that's
  // already set up for unread-pill clearing — we just grow its responsibilities.
  //
  // 2026-04-17 — DO NOT auto-paginate during the initial-load lock window.
  // Bug we hit: opening a thread snaps scrollTop=scrollHeight, but right at
  // that instant image bubbles haven't laid out so scrollHeight ≈ clientHeight,
  // which means scrollTop clamps to ~0. The programmatic scroll fires the
  // scroll event, the handler reads scrollTop ≈ 0, decides we're "near the
  // top", calls triggerLoadOlder. Older messages prepend, pagAnchorRef
  // restores us to 0, user lands looking at messages from days ago. The lock
  // window gate makes this physically impossible — pagination only fires
  // after the layout has settled and the user actually scrolled.
  const triggerLoadOlder = async () => {
    if (!chatId || !phone) return;
    // T13 — if the user is sticking to the bottom, they haven't asked
    // for older messages. This replaces the old 2.5-5s wall-clock lock
    // which had to estimate "when media has probably settled"; the
    // intent-based flag needs no timing guess.
    if (stickToBottomRef.current) return;
    const state = getPaginationState(phone);
    if (state.done || state.loading) return;
    const el = containerRef.current;
    if (!el) return;

    // Capture distance-from-bottom BEFORE the fetch resolves. After
    // prepending, scrollHeight grows by N * bubble_height; restoring the
    // same distance-from-bottom keeps the currently-visible content in place.
    pagAnchorRef.current = el.scrollHeight - el.scrollTop;
    setLoadingOlder(true);
    try {
      const added = await loadOlderMessages(chatId, phone);
      // After the store update, React re-renders and the layout effect
      // below restores the scroll position using pagAnchorRef.
      if (added === 0) pagAnchorRef.current = null;
    } finally {
      setLoadingOlder(false);
      const s = getPaginationState(phone);
      if (s.done) setPagDone(true);
    }
  };

  // Smart auto-scroll. Runs after every render that changed message count.
  //   - Initial load (prev=0): start the "initial-load lock window" — see
  //     the ResizeObserver effect below. While the window is open every
  //     container resize re-snaps to bottom, which catches all the
  //     async growth waves (image bubbles loading bytes, late reactions
  //     hydrating, system dividers asyncly populating).
  //   - Pagination prepend (pagAnchorRef set): restore distance-from-bottom
  //     so the currently-visible content stays put.
  //   - Subsequent appends: only yank if the user was already near the
  //     bottom. Otherwise bump the "↓ N new" pill count.
  //
  // 2026-04-17 v3 — replaced the fragile rAF/setTimeout ladder with a
  // wall-clock lock + ResizeObserver. Previous attempts gated re-yank
  // on _isNearBottom, but the moment ANY image grew the container the
  // slack exceeded the threshold and the ladder bailed — exactly when
  // it needed to fire. Lock + RO is unconditional within the window
  // and respects user scrolls outside it.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const prev = prevMsgCountRef.current;
    const curr = messages.length;
    prevMsgCountRef.current = curr;

    if (curr === 0) return;
    if (prev === 0) {
      // Initial load — snap to bottom + arm stickiness. T13 replaced
      // the prior wall-clock lock (5s) with user-intent tracking.
      // The ResizeObserver below now re-snaps on every container
      // growth while stickToBottomRef is true, so media loading
      // minutes later still keeps the viewport anchored. No lock
      // expiry to fight; scrolling up manually flips off stickiness
      // via the scroll handler.
      el.scrollTop = el.scrollHeight;
      stickToBottomRef.current = true;
      setUnreadBelow(0);
      return;
    }
    if (curr <= prev) return; // nothing new (or a splice/reconcile)

    // Pagination case: older bubbles were prepended to the head. Restore
    // the distance-from-bottom anchor captured in triggerLoadOlder so the
    // visible viewport content doesn't jump.
    if (pagAnchorRef.current != null) {
      el.scrollTop = el.scrollHeight - pagAnchorRef.current;
      pagAnchorRef.current = null;
      return;
    }

    if (wasNearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      // Image-grow safety net for incremental appends — re-snap once on
      // the next frame in case a media bubble was the new message.
      requestAnimationFrame(() => {
        if (containerRef.current && _isNearBottom(containerRef.current)) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      });
      setUnreadBelow(0);
    } else {
      // How many newly-appended bubbles should we claim as "unread"? We only
      // count strictly-new messages (curr - prev) and skip outbound ones —
      // seeing your own sent messages doesn't feel like "unread" to users.
      let added = 0;
      for (let i = prev; i < curr; i++) {
        const m = messages[i];
        if (m && m.direction !== DIRECTION.OUTBOUND) added += 1;
      }
      if (added > 0) setUnreadBelow(n => n + added);
    }
  // 2026-04-17 v4 — CRITICAL: loading MUST be in the deps array. The
  // messages store update (setMessages) and the loading state change
  // (setLoading(false)) happen in TWO SEPARATE renders:
  //   Render 1: messages.length 0→50, loading still true → container NOT
  //             in DOM → containerRef null → effect exits early.
  //   Render 2: loading true→false → container appears → BUT this effect
  //             doesn't fire (messages.length didn't change).
  // Adding `loading` to the deps makes the effect re-fire on render 2,
  // sees prev still=0 (wasn't updated on render 1 because el was null),
  // enters the initial-load path, and snaps to bottom. THIS is the
  // actual root cause behind the "thread opens at April 15" symptom.
  }, [messages.length, loading]);

  // T13 — Resize observer: re-snap to bottom on ANY container size
  // change while the user is sticking to the bottom. Previously gated
  // by a 5s wall-clock lock which failed when images finished loading
  // after 5s on slow networks — the "thread opens at older messages"
  // symptom. Gating on stickToBottomRef.current instead means:
  //   - User at bottom + image loads 30s later → snap ✓
  //   - User scrolls up to read history → stickToBottomRef=false → no
  //     fight on subsequent image loads ✓
  //   - User scrolls back down → near-bottom check re-enables sticky
  //     via the scroll handler → snap resumes ✓
  //
  // Same loading-dep handling as before: the container isn't in the
  // DOM at mount (loading=true), so the observer re-attaches when
  // loading flips to false.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      if (!containerRef.current) return;
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // Scroll listener — two responsibilities:
  //   1. Clear the unread pill when the user returns to the bottom
  //   2. Fire the load-older pagination trigger when they hover near the top
  // Both are cheap reads of scrollTop / scrollHeight so running them on
  // every scroll tick is fine (the browser already throttles scroll events).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const TOP_TRIGGER_PX = 180;
    const onScroll = () => {
      // T13 — keep stickToBottomRef in sync with user intent. Any time
      // the user is within the "near bottom" band we stick; any time
      // they aren't, we don't. Updated on every scroll so a user-
      // initiated scroll up flips it off, and scrolling back down
      // flips it back on (at which point the RO above resumes
      // anchoring to the latest content as it grows).
      const nearBottom = _isNearBottom(el);
      stickToBottomRef.current = nearBottom;
      if (nearBottom) setUnreadBelow(0);
      if (el.scrollTop < TOP_TRIGGER_PX) {
        // triggerLoadOlder is idempotent (state.loading / state.done guards)
        // so it's safe to call on every tick while the user lingers at the top.
        triggerLoadOlder();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [chatId]);

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // T13 — user explicitly asked to go to the latest. Re-arm the
    // sticky flag so the RO keeps anchoring through any subsequent
    // media loads.
    stickToBottomRef.current = true;
    setUnreadBelow(0);
  };

  // #161 — click a reply-quote header to smooth-scroll to the target
  // and flash a highlight pulse. Matches native WhatsApp behavior.
  //
  // DOM lookup uses two attributes on every bubble wrapper:
  //   data-wamid       — primary render key (for outbound rows this
  //                      is our queue_id; for inbound it's the Meta
  //                      wamid)
  //   data-meta-wamid  — Meta wamid stamped on outbound rows after
  //                      the status webhook arrives
  //
  // Customer replies reference the Meta wamid via context.message_id,
  // so we check both. For the "target beyond the loaded window" case
  // we lazy-load older pages one at a time (up to MAX_JUMP_PAGES =
  // 10 = ~500 messages backward) and re-check after each page lands.
  // Same pattern WhatsApp uses: tap a reply on an old message and it
  // transparently walks history until it finds it.
  const MAX_JUMP_PAGES = 10;

  const _findBubble = (wamid) => {
    const container = containerRef.current;
    if (!container) return null;
    const escaped = CSS.escape(wamid);
    return container.querySelector(`.inbox-bubble-wrap[data-wamid="${escaped}"]`)
        || container.querySelector(`.inbox-bubble-wrap[data-meta-wamid="${escaped}"]`);
  };

  const _flashBubble = (target) => {
    const container = containerRef.current;
    if (!target || !container) return;
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      const rect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      container.scrollTop += rect.top - containerRect.top - container.clientHeight / 2;
    }
    target.classList.remove('inbox-bubble-jump-flash');
    void target.offsetWidth;  // force reflow → animation restarts
    target.classList.add('inbox-bubble-jump-flash');
    setTimeout(() => target.classList.remove('inbox-bubble-jump-flash'), 1600);
  };

  const jumpToMessage = async (targetWamid) => {
    const container = containerRef.current;
    if (!container || !targetWamid) return;

    // Fast path — target is already loaded.
    const immediate = _findBubble(targetWamid);
    if (immediate) { _flashBubble(immediate); return; }

    // Slow path — auto lazy-load older pages until the target lands
    // or we hit the bound / end of history. Show a small "hunting"
    // toast so the agent knows something is happening; tapping an
    // ancient reply should feel like "it's working" rather than
    // "nothing happened".
    showToast('Loading older messages…', 1200);

    for (let i = 0; i < MAX_JUMP_PAGES; i++) {
      const state = getPaginationState(phone);
      if (state.done) break;

      // Capture distance-from-bottom anchor so the existing pagination
      // restore logic in the smart-scroll effect doesn't yank us around
      // mid-hunt. We're going to override the scroll position anyway
      // once we find the target, so we don't need to preserve it.
      const before = container.scrollHeight;

      let added;
      try {
        added = await loadOlderMessages(chatId, phone);
      } catch (e) {
        console.warn('[v2 thread] jumpToMessage load-older failed:', e.message);
        break;
      }
      if (added === 0) break;  // end of history

      // Give the DOM a frame to commit the new bubbles, then re-check.
      // Preact renders synchronously from store updates but the browser
      // still needs one paint tick to flush layout so querySelector
      // finds the new wrappers.
      await new Promise(r => requestAnimationFrame(() => r()));
      const hit = _findBubble(targetWamid);
      if (hit) {
        _flashBubble(hit);
        return;
      }
      // Keep the user's viewport visually anchored at the top while we
      // hunt — avoids a disorienting double-jump at the end.
      container.scrollTop = Math.max(0, container.scrollHeight - before);
    }

    // Bounded hunt exhausted. Show a clearer toast so the agent knows
    // the target is too old / too deep rather than thinking the click
    // did nothing.
    showToast('Original message too old to load in this session', 2400);
  };

  // 24-hour session window check. Computed every render so it stays fresh
  // as the user sits on the screen (the value only flips once per hour
  // boundary anyway — cheap).
  const sessionStatus = _sessionWindowStatus(chat?.lastInboundTs);
  const windowExpired = !!sessionStatus?.expired;
  // #190 (revised) — block sends when offline. Prefer the more specific
  // expired-window message when both apply, since once the customer
  // re-messages the window opens and the offline state is the actionable
  // remaining concern.
  const online       = useStore($isOnline);
  const composerOff  = windowExpired || !online;
  const composerWhy  = windowExpired
    ? '24-hour window closed — wait for the customer to message again'
    : (!online
        ? "You're offline — wait until the connection comes back"
        : null);

  // #167 — ephemeral hand-off divider: rendered once at the bottom of the
  // thread when a transfer-with-note has just completed. In-memory only,
  // keyed by `${chatId}:${channelPhoneId}`. Cleared on browser refresh /
  // thread close; the persistent copy lives in the notes timeline.
  const handoffMap   = useStore($ephemeralHandoffNote);
  const handoffNote  = (chatId && channelPhoneId)
    ? handoffMap[`${chatId}:${channelPhoneId}`] || null
    : null;

  // #160 — resolve current assignee for the read-only ownership banner.
  // Re-render when the team directory hydrates so the banner text flips
  // from "Agent · 69dc2cde" to the real name after /team-directory lands.
  const team = useStore($team);
  // Gate the banner on team hydration. Before /team-directory lands, we
  // can't tell whether a bare ObjectId belongs to the logged-in owner
  // (owner heuristic needs owner.email + selfEmail to fire isYou=true),
  // so resolveAgent returns isYou=false by default — which would falsely
  // render the banner for ~200ms until hydration completes and the
  // banner flips off. That flicker reshapes the layout mid-scroll-pin
  // and causes the "thread scrolls up on open" jolt the user reported.
  // Waiting for hydration eliminates the flicker entirely.
  const teamHydrated = !!(team.selfEmail || (team.byId && team.byId.size > 0));
  const currentAssignee = chat?.agent ? resolveAgent(chat.agent) : null;
  const showOwnershipBanner = teamHydrated
    && !!(currentAssignee && !currentAssignee.isYou);

  // #162: the old inline 60s-tick HH:MM timer moved into its own
  // SessionCountdown component (HH:MM:SS, 1s tick, blinking colons,
  // isolated re-renders so the message list doesn't thrash). The JSX
  // below just drops <SessionCountdown lastInboundTs=${...}/>.

  // #160 — pending-action queue. When an action (send text, react,
  // reply) runs against a chat assigned to another agent, we stash it
  // here, show the transfer-confirmation modal, and replay the action
  // after the user confirms. Cancelling clears the queue and the
  // composer keeps whatever the user had typed.
  const [transferPrompt, setTransferPrompt] = useState(null);
  // shape: { currentAssignee, run: () => Promise<void> }

  // #162 — explicit actions from the header.
  // Team picker sheet: visible when the agent taps the transfer button.
  const [pickerOpen, setPickerOpen] = useState(false);
  // Done confirmation sheet: visible when Done is tapped on a chat
  // currently owned by someone else (we still need a confirmation
  // there, because taking someone's chat and marking it done is rude).
  // When the chat is unassigned or already owned by self, Done fires
  // instantly with an undo toast.
  const [doneConfirm, setDoneConfirm] = useState(null);

  // Shared gate for every action that implies intent. Returns true if
  // the action can proceed immediately (unassigned → silent claim, or
  // already-mine). Returns false when we've shown the modal — the
  // caller should abort its own path; the modal's "Transfer" button
  // will re-invoke the action after assignment succeeds.
  const _gateAction = (actionRunner) => {
    const decision = _assignmentDecision();
    if (decision.kind === 'deny') {
      showToast('Session expired — please reload', 2200);
      return false;
    }
    if (decision.kind === 'proceed') {
      if (decision.willClaim) _claimForSelf(chatId, phone, channelPhoneId);
      return true;
    }
    // kind === 'confirm' — open the transfer modal, queue the action.
    setTransferPrompt({
      currentAssignee: decision.currentAssignee,
      run: async () => {
        try {
          await _transferToSelf(chatId, phone, channelPhoneId);
        } catch (e) {
          console.warn('[Thread] transfer failed:', e.message);
          showToast('Transfer failed', 1800);
          return;
        }
        setTransferPrompt(null);
        await actionRunner();
      },
    });
    return false;
  };

  const handleSend = async (text, reply) => {
    if (!phone) return;
    const doSend = async () => {
      try {
        await sendText(phone, text, reply?.wamid || null);
      } catch (e) {
        console.error('[Thread] send failed:', e.message);
      }
    };
    // #160: assign-on-intent gate. Returns false when the modal opens
    // to wait for user confirmation; in that case the modal's run()
    // callback will re-invoke the send after transfer completes.
    if (!_gateAction(doSend)) return;
    await doSend();
  };

  const handleSchedule = async (scheduleType, delayMs, triggerOffsetMs, payload) => {
    if (!phone) return;
    try {
      const result = await api.createScheduledMessage({
        chat_id: phone,
        channel_id: channelPhoneId || null,
        schedule_type: scheduleType,
        message_type: payload.type || 'text',
        payload,
        delay_ms: delayMs,
        trigger_offset_ms: triggerOffsetMs,
      });
      const created = result?.data || result;
      if (created?.id) {
        addScheduled(schedKey, created);
      }
      const eta = delayMs
        ? new Date(Date.now() + delayMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : 'before session expires';
      showToast(`Scheduled for ${eta}`, 2500);
    } catch (e) {
      showToast(`Schedule failed: ${e.message}`, 3500);
    }
  };

  const handleCancelScheduled = async (msgId) => {
    removeScheduled(schedKey, msgId);
    try {
      // T4 — proxy distinguishes real cancellation from a no-op on a
      // terminal row. On no-op, toast the ACTUAL status (sent / failed)
      // instead of lying "Cancelled" — users were manually cancelling
      // stale ghosts thinking it worked when the row had already sent.
      const resp = await api.cancelScheduledMessage(msgId);
      const data = resp?.data || resp || {};
      if (data.success === false && data.reason === 'not_pending') {
        const actual = data.status || 'processed';
        const copy = actual === 'sent' ? 'Already sent' : actual === 'failed' ? 'Already failed' : 'Already ' + actual;
        showToast(copy, 2000);
      } else {
        showToast('Scheduled message cancelled', 1500);
      }
    } catch {
      // Transport error — re-fetch ground truth so the UI doesn't lie.
      loadScheduled(phone, channelPhoneId);
      showToast('Cancel failed — refreshed', 2000);
    }
  };

  const handleCancelAllScheduled = async () => {
    clearScheduled(schedKey);
    try {
      await api.cancelAllScheduledMessages(phone);
      showToast('All scheduled messages cancelled', 1500);
    } catch {
      loadScheduled(phone, channelPhoneId);
    }
  };

  // T2 — Retry a failed scheduled row by recreating it with a +60s
  // delay. We deliberately do NOT reopen the popover; "one tap = retry"
  // is the whole point of the inline action in the red ghost state.
  const handleRetryScheduled = async (failedRow) => {
    if (!failedRow?.payload || !phone) return;
    removeScheduled(schedKey, failedRow.id);
    try {
      const result = await api.createScheduledMessage({
        chat_id:       phone,
        channel_id:    channelPhoneId || null,
        schedule_type: 'delay',
        message_type:  failedRow.message_type || 'text',
        payload:       failedRow.payload,
        delay_ms:      60000,
      });
      const created = result?.data || result;
      if (created?.id) addScheduled(schedKey, created);
      showToast('Retrying in 1 min', 2000);
    } catch (e) {
      showToast(`Retry failed: ${e.message}`, 3500);
      loadScheduled(phone, channelPhoneId);
    }
  };

  // T2 — Dismiss a failed ghost locally without hitting the server. The
  // row is already terminal on the backend; this just clears the UI
  // so the user can move on.
  const handleDismissScheduled = (msgId) => {
    removeScheduled(schedKey, msgId);
  };

  // T8 — Send now: frontend-only resolution. The scheduled row is
  // cancelled server-side; its text reloads into the composer so the
  // agent can review and tap Send for the actual dispatch. Deliberately
  // NOT auto-sending — that would surprise the user, and the whole
  // point of scheduling was to let the agent revisit the message before
  // it goes out. One explicit extra tap is the contract.
  //
  // Scope: delay-type queued ghosts only. Keep-alive rows adjust their
  // send time dynamically, so "Send now" would be a different UX
  // (essentially "send without waiting for inbound") and we haven't
  // scoped that.
  const handleSendNowScheduled = async (sm) => {
    if (!sm?.id || !sm.payload?.text?.body) return;
    // Local removal first so the ghost disappears instantly; we still
    // hit the cancel API to make the DB match. If the cancel fails
    // (already-sent race), the load effect will rehydrate — but by
    // then the composer already has the text and the agent can decide.
    removeScheduled(schedKey, sm.id);
    api.cancelScheduledMessage(sm.id).catch(() => {});
    setPrefillSignal({ text: sm.payload.text.body, nonce: Date.now() });
  };

  const handleReply = (msg) => {
    const text = _quotePreview(msg);
    // Same wamid-priority rule as reactions (#132): for outbound messages
    // msg.wamid is our queue_id and Meta would reject context.message_id.
    // Prefer msg.metaWamid (real Meta wamid, stamped after the status
    // webhook arrives). Inbound messages have wamid == Meta wamid already.
    const targetWamid = msg.metaWamid || msg.wamid || msg.id;
    setReplyTo({
      wamid:   targetWamid,
      preview: text.length > 90 ? text.slice(0, 90) + '…' : text,
      fromMe:  !!msg.fromMe || msg.direction === DIRECTION.OUTBOUND,
    });
  };

  // #162 — explicit transfer to any team member via the header picker.
  // #163 — scoped to this specific (contact, channel) row via channelPhoneId.
  // #167 — picker now FUNNELS through TransferConfirmModal so the agent can
  //        attach an optional hand-off note. Picking a member just stages
  //        the target (`pendingTransfer`); the modal's Confirm button does
  //        the actual assign + writes the lead-notes auto-row server-side.
  const [pendingTransfer, setPendingTransfer] = useState(null);
  // shape: { toAgent: { email, name }, fromAgent: { email, name } | null }

  const handleTransferPick = (entry) => {
    if (!entry?.email) return;
    setPickerOpen(false);
    const live = $currentChat.get();
    const fromAgent = live?.agent ? resolveAgent(live.agent) : null;
    setPendingTransfer({
      toAgent:   { email: entry.email, name: entry.name || null },
      fromAgent: fromAgent && fromAgent.email ? { email: fromAgent.email, name: fromAgent.name || null } : null,
    });
  };

  const handleTransferConfirm = async (handoffNote) => {
    const target = pendingTransfer?.toAgent;
    if (!target?.email) return;
    const live = $currentChat.get();
    const chatKey = live?.id || null;
    const prevAgent = live?.agent || null;
    // Optimistic — patch just this row by its composite key.
    if (chatKey) updateChat(chatKey, { agent: target.email, isDone: false });
    try {
      // Pass agent_name + previous_agent_email so the server-side audit
      // (system divider + lead-notes auto-row) gets the right names and
      // marks this as kind='transfer'. fromAgent comes from the modal's
      // pendingTransfer state, captured before the handoff started.
      const fromEmail = pendingTransfer?.fromAgent?.email || prevAgent || null;
      await api.assignChat(chatId, phone, target.email, channelPhoneId, {
        handoffNote,
        agentName:           target.name || target.email,
        previousAgentEmail:  fromEmail,
        customerName:        live?.name || null,
      });
      // #167 — stash the ephemeral divider so the same tab (and the
      // outgoing agent) sees the hand-off context one-shot below the
      // latest message. Cleared on refresh / thread close.
      if (handoffNote && handoffNote.trim()) {
        const sess = getSession();
        setEphemeralHandoffNote(chatId, channelPhoneId, {
          fromName:  sess?.agentName  || sess?.agentEmail || 'You',
          fromEmail: sess?.agentEmail || null,
          toName:    target.name || target.email,
          toEmail:   target.email,
          body:      handoffNote.trim(),
          ts:        Date.now(),
        });
      }
      // Reload the notes timeline so the auto-row server-side write
      // shows up the next time the agent opens the sheet.
      loadNotes(chatId, channelPhoneId).catch(() => {});
      showToast(`Transferred to ${target.name || target.email}`, 2400);
      setPendingTransfer(null);
    } catch (e) {
      if (chatKey) updateChat(chatKey, { agent: prevAgent });
      console.warn('[Thread] transfer failed:', e.message);
      // Rethrow so the modal renders the error inline (it manages its
      // own busy state and will re-enable the buttons).
      throw e;
    }
  };

  const handleTransferRelease = async () => {
    setPickerOpen(false);
    const live = $currentChat.get();
    const chatKey = live?.id || null;
    const prevAgent = live?.agent || null;
    if (chatKey) updateChat(chatKey, { agent: null });
    try {
      // Empty email = unassign this row. Proxy reconciles CRM to any
      // other still-assigned row's agent (or truly unassigns if none).
      await api.assignChat(chatId, phone, '', channelPhoneId);
      showToast('Chat released to queue', 2200);
    } catch (e) {
      if (chatKey) updateChat(chatKey, { agent: prevAgent });
      console.warn('[Thread] release failed:', e.message);
      showToast('Release failed', 2000);
    }
  };

  // #162/#163 — mark THIS row as done. Optimistic patch clears the
  // row's agent and sets isDone=true; the proxy reconciles CRM only
  // when no other row on this contact still has an agent (so bots
  // stay paused on still-active channels).
  const _fireDone = async () => {
    const live = $currentChat.get();
    const chatKey = live?.id || null;
    if (chatKey) updateChat(chatKey, { agent: null, isDone: true });
    try {
      await api.markDone(chatId, phone, channelPhoneId);
      showToast('Chat marked as done', 2400);
    } catch (e) {
      console.warn('[Thread] markDone failed:', e.message);
      showToast('Mark as done failed', 2000);
    }
  };

  const handleDone = () => {
    const live = $currentChat.get();
    if (!live) return;
    if (live.agent && !resolveAgent(live.agent).isYou) {
      // Owned by a different agent — confirm before taking the action.
      setDoneConfirm({
        currentAssignee: resolveAgent(live.agent),
      });
      return;
    }
    // Unassigned or already mine → instant path.
    _fireDone();
  };

  const handleReact = async (msg, emoji) => {
    if (!phone) return;
    // Reactions must reference the REAL Meta wamid as reaction.message_id —
    // Meta silently drops reactions targeting unknown ids. For inbound
    // messages msg.wamid is already the Meta wamid (set straight from
    // wa.id by the inbox webhook). For outbound messages we generate a
    // queue_id locally and stamp the Meta wamid into msg.metaWamid only
    // after the status webhook arrives, so prefer metaWamid when present.
    const targetWamid = msg.metaWamid || msg.wamid || msg.id;
    if (!targetWamid) {
      console.warn('[Thread] reaction skipped — no wamid on target', msg);
      return;
    }
    const doReact = async () => {
      try {
        await sendReaction(phone, targetWamid, emoji);
      } catch (e) {
        console.error('[Thread] reaction failed:', e.message);
      }
    };
    // #160: reactions count as intent. Gate them through the same
    // assignment flow as text sends.
    if (!_gateAction(doReact)) return;
    await doReact();
  };

  return html`
    <div class="inbox-screen" style="display:flex">
      <!-- Header (with channel-color accent line on the bottom edge — same
           visual hint language as the chat row's left-edge stripe) -->
      <div class="inbox-header" style=${channelColor ? `box-shadow:inset 0 -2px 0 0 ${channelColor}` : ''}>
        <button class="inbox-header-icon-btn" onClick=${closeThread} title="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>

        <!-- Contact identity block: avatar + stacked name/phone. Tappable —
             opens the contact panel just like the (i) button on the right,
             so the agent can hit the bigger target instead of aiming at
             the small icon. -->
        <button
          class="inbox-thread-contact"
          onClick=${() => setContactPanelOpen(true)}
          title="Contact info"
          aria-label="Open contact info"
          type="button"
        >
          ${(() => {
            const name = chat?.name || phone || 'Chat';
            const initials = String(name)
              .split(/\s+/).filter(Boolean).slice(0, 2)
              .map(w => w[0]?.toUpperCase() || '').join('') || '#';
            // Same session-window logic as the chat-list row dot — green when
            // the customer's 24h window is open, red when closed (template-only
            // territory, customer is unreachable until they reply). 'unknown'
            // (no inbound history) → no dot.
            const sk = sessionKind(chat?.lastInboundTs);
            const isOpen   = sk === 'open';
            const isClosed = sk === 'closed';
            const dotStyle = isOpen
              ? 'background:#25D366;box-shadow:0 0 0 1px rgba(37,211,102,0.35)'
              : 'background:#ef4444;box-shadow:0 0 0 1px rgba(239,68,68,0.35)';
            const dotTitle = isOpen
              ? 'Session window open'
              : '24-hour window closed — customer is unreachable until they message back';
            return html`
              <span
                class="inbox-thread-avatar"
                style=${channelColor ? `background:${channelColor}1f;color:${channelColor}` : ''}
                aria-hidden="true"
              >
                ${initials}
                ${(isOpen || isClosed) && html`<span
                  class="inbox-thread-avatar-dot"
                  style=${dotStyle}
                  title=${dotTitle}
                  aria-hidden="true"
                ></span>`}
              </span>
            `;
          })()}
          <span class="inbox-thread-contact-text">
            <span class="inbox-thread-contact-name">${chat?.name || phone || 'Chat'}</span>
            <span class="inbox-thread-contact-sub">
              ${phone ? html`<span class="inbox-thread-contact-phone">${_formatHeaderPhone(phone)}</span>` : null}
              ${phone && chat?.lastInboundTs ? html`<span class="inbox-thread-sub-sep" aria-hidden="true">·</span>` : null}
              <!-- #162 — live HH:MM:SS countdown lives inline in the sub
                   line. Isolated 1s tick keeps re-renders to ~4 nodes. -->
              <${SessionCountdown} lastInboundTs=${chat?.lastInboundTs} />
            </span>
          </span>
        </button>

        <!-- #162 — Transfer (⇄). Opens the team picker sourced from the
             CRM roster. Frequent action → always-visible header button. -->
        <button
          class="inbox-header-icon-btn"
          onClick=${() => setPickerOpen(true)}
          title="Transfer chat"
          aria-label="Transfer chat to another agent"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="17 1 21 5 17 9"/>
            <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
            <polyline points="7 23 3 19 7 15"/>
            <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
          </svg>
        </button>

        <!-- #167 — Notes. Internal lead notes per (contact, channel) row.
             Stays usable when the 24h window is closed because notes are
             team-internal, not customer-facing. -->
        <button
          class="inbox-header-icon-btn"
          onClick=${() => $sheets.setKey('notes', true)}
          title="Internal notes"
          aria-label="Open internal notes for this chat"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="9" y1="13" x2="15" y2="13"/>
            <line x1="9" y1="17" x2="13" y2="17"/>
          </svg>
        </button>

        <!-- #162 — Mark as Done (✓). Frequent end-of-conversation action.
             Instant path for self-owned or unassigned chats; confirmation
             when the chat belongs to a different agent. -->
        <button
          class="inbox-header-icon-btn"
          onClick=${handleDone}
          title="Mark as done"
          aria-label="Mark chat as done"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </button>

        <!-- Contact info (i). Catch-all for tags, CF, Close, etc. -->
        <button class="inbox-header-icon-btn" onClick=${() => setContactPanelOpen(true)} title="Contact info">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </button>
      </div>

      <!-- #160 — read-only ownership banner. Shown when the current chat
           is assigned to someone other than the logged-in agent. Makes
           it crystal-clear to team leads / colleagues reading history
           that they're viewing someone else's conversation. The banner
           is dismissed automatically the moment the agent takes an
           action (which triggers the transfer modal or claims the
           unassigned row silently). -->
      ${showOwnershipBanner && html`
        <div
          class="inbox-ownership-banner"
          style="display:flex;align-items:center;gap:8px;padding:7px 14px;background:rgba(96,165,250,0.08);border-bottom:1px solid rgba(96,165,250,0.22);color:#93c5fd;font-size:0.75rem;line-height:1.3"
          role="status"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8"  x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>
            Assigned to <b style="color:#dbeafe">${currentAssignee.label}</b> · you're viewing read-only
          </span>
        </div>
      `}

      <!-- Messages -->
      <div class="inbox-messages-container" ref=${containerRef}>
        ${loading && html`
          <div class="inbox-loading-state">
            <div class="inbox-spinner"></div>
            <span>Loading messages…</span>
          </div>
        `}

        ${error && !loading && html`
          <div class="inbox-empty-state">
            <p style="color:var(--text-muted)">Could not load messages</p>
            <p style="color:var(--text-muted);font-size:0.8rem">${error}</p>
          </div>
        `}

        ${!loading && !error && messages.length === 0 && html`
          <div class="inbox-empty-state">
            <p>No messages yet</p>
          </div>
        `}

        <!-- Load-older indicator — sits at the top of the scroll region so
             users see it when they scroll up past the oldest rendered bubble.
             The spinner is live while a pagination fetch is in flight; the
             end-of-conversation pill replaces it permanently once we've seen
             the oldest message. -->
        ${!loading && messages.length > 0 && loadingOlder && html`
          <div style="display:flex;justify-content:center;padding:10px 0;opacity:0.8">
            <div class="inbox-spinner" style="width:16px;height:16px;border-width:2px"></div>
            <span style="margin-left:8px;font-size:0.75rem;color:var(--text-muted, #7d8799)">Loading older messages…</span>
          </div>
        `}
        ${!loading && messages.length > 0 && !loadingOlder && pagDone && html`
          <div style="display:flex;justify-content:center;padding:10px 0">
            <span style="font-size:0.68rem;color:var(--text-muted, #7d8799);background:rgba(255,255,255,0.05);padding:4px 12px;border-radius:10px;letter-spacing:0.02em">
              Start of conversation
            </span>
          </div>
        `}

        ${!loading && (() => {
          // Build a wamid → message index for reply-quote lookups. Both wamid
          // (queue_id for outbound, Meta wamid for inbound) AND metaWamid
          // (only set on outbound rows after a status event has correlated)
          // are indexed so customer replies to agent messages resolve via
          // the Meta wamid that Meta references in the reply context.
          const wamidIndex = new Map();
          for (const m of messages) {
            if (m.wamid)     wamidIndex.set(m.wamid, m);
            if (m.metaWamid) wamidIndex.set(m.metaWamid, m);
          }

          // Lead-origin card: when the CRM custom fields carry a
          // lead_source_ui, we render a discreet in-timeline card after
          // the customer's first inbound text bubble that surfaces HOW
          // they reached us (sticky bar, WhatsApp keyword, QR code, ad
          // referral, etc). We deliberately do not surface a template
          // name or campaign attribution: outbound marketing templates
          // from Partner Panel campaigns never echo to inbox_messages,
          // so we don't actually know which template (if any) was sent.
          // Showing a guessed name based on utm_term misleads — leads
          // attributed to the same campaign can arrive via different
          // routes (sticky bar vs. wa.me keyword) and receive different
          // (or no) templates.
          const source  = chat?.customFieldHash?.lead_source_ui || '';
          let triggerIdx = -1;
          if (source) {
            for (let k = 0; k < messages.length; k++) {
              if (messages[k].direction === DIRECTION.INBOUND
                  && (messages[k].type === 'text' || messages[k].type === 'chat')) {
                triggerIdx = k;
                break;
              }
            }
          }

          // Precompute render items in one pass — dividers + bubbles inline.
          // We need lookahead/lookbehind to (a) decide if a date divider
          // should appear before this bubble, and (b) make sure bubble
          // grouping doesn't visually span across a day boundary.
          const items = [];
          for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            const prev = messages[i - 1];
            const next = messages[i + 1];

            const dThis = _toDate(msg.timestamp);
            const dPrev = prev ? _toDate(prev.timestamp) : null;
            const dNext = next ? _toDate(next.timestamp) : null;

            const dayChanged     = !!dThis && (!dPrev || !_sameDay(dThis, dPrev));
            const nextDayChanged = !!dNext && !_sameDay(dThis, dNext);

            // Insert a date divider before this bubble whenever the day rolls
            if (dayChanged && dThis) {
              items.push(html`
                <div
                  key="div-${dThis.toDateString()}"
                  class="inbox-date-divider"
                  style="display:flex;justify-content:center;align-items:center;margin:14px 0 6px"
                >
                  <span style="background:rgba(255,255,255,0.07);color:rgba(255,255,255,0.7);font-size:0.72rem;padding:4px 12px;border-radius:12px;letter-spacing:0.02em">
                    ${_formatDayLabel(msg.timestamp)}
                  </span>
                </div>
              `);
            }

            // Bubble grouping: same direction, within 2 minutes, AND same day.
            // Day-change forces grouping off so visual chains don't leak
            // across the divider.
            const sameSenderAsPrev = prev
              && prev.direction === msg.direction
              && msg.direction !== DIRECTION.SYSTEM && prev.direction !== DIRECTION.SYSTEM
              && Math.abs((msg.timestamp || 0) - (prev.timestamp || 0)) < 120
              && !dayChanged;

            const sameSenderAsNext = next
              && next.direction === msg.direction
              && msg.direction !== DIRECTION.SYSTEM && next.direction !== DIRECTION.SYSTEM
              && Math.abs((next.timestamp || 0) - (msg.timestamp || 0)) < 120
              && !nextDayChanged;

            // Resolve reply-target preview if this message is a reply.
            // Falls back to a generic "Original message" if the target
            // isn't in the loaded window (e.g., reply to a very old message
            // beyond the latest page).
            //
            // Bot-handoff special case: when a customer taps a button / picks
            // a list row / submits a native flow form, Meta sets context.id
            // to the original outbound prompt. If that prompt was sent by an
            // external bot platform (e.g. chat.odivend), it never landed in
            // inbox_messages — wamidIndex.get() misses and we'd render a
            // useless "Customer / Original message" quote on top of a bubble
            // whose body already shows the customer's selection. Suppress
            // the quote in that case; the bubble body is self-explanatory.
            let replyToPreview = null;
            if (msg.replyTo) {
              const target = wamidIndex.get(msg.replyTo);
              if (target) {
                const text = _quotePreview(target);
                replyToPreview = {
                  text:   text.length > 90 ? text.slice(0, 90) + '…' : text,
                  fromMe: !!target.fromMe || target.direction === DIRECTION.OUTBOUND,
                  found:  true,
                };
              } else {
                const it = msg.interactive;
                const isBotReply = msg.type === 'interactive' &&
                  (it?.button_reply || it?.list_reply || it?.nfm_reply);
                if (!isBotReply) {
                  replyToPreview = { text: 'Original message', fromMe: false, found: false };
                }
              }
            }

            items.push(html`
              <${MessageBubble}
                key=${msg.id || msg.wamid || i}
                msg=${msg}
                phone=${phone}
                channelPhoneId=${channelPhoneId}
                replyToPreview=${replyToPreview}
                groupedTop=${sameSenderAsPrev}
                groupedBottom=${sameSenderAsNext}
                onReply=${() => handleReply(msg)}
                onReact=${(emoji) => handleReact(msg, emoji)}
                onJumpToReply=${jumpToMessage}
                interactionsLocked=${windowExpired}
              />
            `);

            // Lead-origin card, placed right after the first inbound
            // text bubble. See `source` + triggerIdx setup above. We
            // surface ONLY the lead-source route here — nothing about
            // templates, campaign names, or message content.
            if (i === triggerIdx) {
              const routeLabel = _formatLeadSource(source);
              items.push(html`
                <div
                  key="lead-origin-${triggerIdx}"
                  class="inbox-campaign-echo"
                  role="note"
                  aria-label=${`Lead arrived via ${routeLabel}`}
                >
                  <div class="inbox-campaign-echo-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div class="inbox-campaign-echo-body">
                    <div class="inbox-campaign-echo-eyebrow">Lead Origin</div>
                    <div class="inbox-campaign-echo-name">${routeLabel}</div>
                  </div>
                </div>
              `);
            }
          }
          return items;
        })()}

        <!-- #167 — Ephemeral handoff divider. One-shot context surface for
             the agent who just received (or just made) a transfer with a
             note. Cleared on refresh / thread close; the persistent copy
             lives in the notes sheet. -->
        ${handoffNote && html`
          <div class="inbox-handoff-divider" role="note">
            <div class="inbox-handoff-divider-line"></div>
            <div class="inbox-handoff-divider-card">
              <div class="inbox-handoff-divider-meta">
                Handoff from <strong>${handoffNote.fromName}</strong>
                ${handoffNote.toName ? html` to <strong>${handoffNote.toName}</strong>` : null}
              </div>
              <div class="inbox-handoff-divider-body">${handoffNote.body}</div>
              <button
                class="inbox-handoff-divider-dismiss"
                onClick=${() => clearEphemeralHandoffNote(chatId, channelPhoneId)}
                aria-label="Dismiss handoff note"
                title="Dismiss"
              >×</button>
            </div>
            <div class="inbox-handoff-divider-line"></div>
          </div>
        `}
      </div>

      <!-- "↓ N new messages" pill — only visible when the user is scrolled
           up AND new inbound bubbles have arrived since. Tapping it scrolls
           back to the bottom and clears the counter. Sits just above the
           composer, centered horizontally. -->
      ${unreadBelow > 0 && html`
        <button
          class="inbox-unread-pill"
          onClick=${scrollToBottom}
          style="position:absolute;left:50%;bottom:84px;transform:translateX(-50%);background:#25D366;color:#0a0c12;font-weight:600;font-size:0.78rem;padding:7px 14px;border:none;border-radius:999px;box-shadow:0 4px 12px rgba(0,0,0,0.4);cursor:pointer;z-index:20;display:flex;align-items:center;gap:6px"
          title="Jump to latest"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9l6 6 6-6"/>
          </svg>
          ${unreadBelow} new ${unreadBelow === 1 ? 'message' : 'messages'}
        </button>
      `}

      <!-- 24-hour session window guard. When the customer's last inbound
           message is >24h old, WhatsApp rejects freeform outbound. Per the
           product decision, the agent inbox does NOT offer template
           re-engagement — outbound campaigns belong to marketing surfaces.
           This banner is informational only; the composer (and reactions)
           are locked via the disabled flag below. -->
      ${windowExpired && html`
        <div
          class="inbox-session-expired-bar"
          style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:rgba(245,158,11,0.12);border-top:1px solid rgba(245,158,11,0.35);color:#fde68a;font-size:0.78rem;line-height:1.35"
          role="status"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;color:#fef3c7">24-hour window closed</div>
            <div style="opacity:0.8">Last reply was ${sessionStatus.label}. Wait for the customer to message again before responding.</div>
          </div>
        </div>
      `}

      <!-- Scheduled ghost bubbles -->
      ${scheduledList.length > 0 && html`
        <div style="padding:4px 12px 8px">
          ${scheduledList.length >= 2 && html`
            <div style="display:flex;align-items:center;justify-content:center;gap:8px;padding:8px 14px;margin-bottom:8px;font-size:0.76rem;color:#7d8799;border-top:1px solid rgba(255,255,255,0.06)">
              <span>${scheduledList.length} scheduled</span>
              <span style="color:rgba(255,255,255,0.2)">·</span>
              <button
                onClick=${handleCancelAllScheduled}
                style="background:none;border:none;color:#f87171;font-weight:600;font-size:0.76rem;cursor:pointer;padding:0;font-family:inherit"
              >Cancel all</button>
            </div>
          `}
          ${scheduledList.map(sm => {
            // T2 — five-state ghost bubble state machine.
            //   - failed   : cron marked failed, red chrome, Retry + Dismiss
            //   - sending  : send_at <= now and still pending, pulsing dot
            //   - queued   : default, countdown (delay) or "Nh before…" (keep_alive)
            // Stuck-state is auto-handled via the realtime broadcast —
            // no local timer needed because T1 guarantees a terminal
            // event within the next cron tick.
            const isKeepAlive = sm.schedule_type === 'keep_alive';
            const isFailed    = sm._uiStatus === 'failed';
            const sendAtMs    = sm.send_at ? new Date(sm.send_at).getTime() : null;
            const isSendingNow = !isFailed && !isKeepAlive && sendAtMs != null && sendAtMs <= Date.now();
            const body = sm.payload?.text?.body || sm.payload?.interactive?.body?.text || '[Message]';

            // Accent color + border color per state
            const accent  = isFailed ? '#f87171' : isKeepAlive ? '#93c5fd' : '#25D366';
            const borderC = isFailed ? 'rgba(248,113,113,0.45)' : isKeepAlive ? 'rgba(96,165,250,0.35)' : 'rgba(37,211,102,0.35)';
            const bgC     = isFailed ? 'rgba(248,113,113,0.06)' : isKeepAlive ? 'rgba(96,165,250,0.04)' : 'rgba(37,211,102,0.04)';

            // Time / status label
            let statusLabel = '';
            if (isFailed) {
              statusLabel = `Failed${sm._failReason ? ' · ' + _shortReason(sm._failReason) : ''}`;
            } else if (isSendingNow) {
              statusLabel = 'Sending…';
            } else if (isKeepAlive) {
              const offsetH = Math.round((sm.trigger_offset_ms || 3600000) / 3600000);
              statusLabel = offsetH + 'h before session ends';
            } else if (sendAtMs != null) {
              const diff = sendAtMs - Date.now();
              if (diff < 60000) statusLabel = 'Sending in <1 min';
              else if (diff < 3600000) statusLabel = 'Sending in ' + Math.ceil(diff / 60000) + ' min';
              else statusLabel = 'Sending in ' + (diff / 3600000).toFixed(1) + 'h';
            }

            // Icon per state
            const iconSvg = isFailed
              ? html`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`
              : isKeepAlive
              ? html`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>`
              : html`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

            // Footer actions per state
            const actions = isFailed
              ? html`
                  <div style="display:flex;gap:14px;margin-top:6px">
                    <button
                      onClick=${() => handleRetryScheduled(sm)}
                      style="font-size:0.72rem;font-weight:700;color:#25D366;background:none;border:none;padding:0;cursor:pointer;font-family:inherit"
                    >Retry</button>
                    <button
                      onClick=${() => handleDismissScheduled(sm.id)}
                      style="font-size:0.72rem;font-weight:600;color:#7d8799;background:none;border:none;padding:0;cursor:pointer;font-family:inherit"
                    >Dismiss</button>
                  </div>
                `
              : isSendingNow
              ? null  // No cancel: CRM may have already accepted
              : html`
                  <div style="display:flex;align-items:center;gap:12px;margin-top:6px">
                    <button
                      onClick=${() => handleCancelScheduled(sm.id)}
                      style="font-size:0.72rem;font-weight:600;color:#f87171;background:none;border:none;padding:0;cursor:pointer;font-family:inherit"
                    >Cancel</button>
                    ${!isKeepAlive && sm.payload?.text?.body && html`
                      <button
                        onClick=${() => handleSendNowScheduled(sm)}
                        aria-label="Send now — load into composer"
                        title="Send now — loads into composer"
                        style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;color:#25D366;background:none;border:none;padding:0;cursor:pointer;border-radius:50%;transition:background 0.15s ease"
                        onMouseOver=${(e) => e.currentTarget.style.background = 'rgba(37,211,102,0.12)'}
                        onMouseOut=${(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                      </button>
                    `}
                  </div>
                `;

            return html`
              <div
                key=${sm.id}
                role=${isFailed ? 'alert' : 'status'}
                aria-live=${isFailed ? 'assertive' : 'polite'}
                aria-busy=${isSendingNow ? 'true' : 'false'}
                style="margin-left:auto;max-width:82%;padding:10px 14px;border:1px dashed ${borderC};border-radius:12px;background:${bgC};margin-bottom:8px"
              >
                <div style="display:flex;align-items:center;gap:6px;font-size:0.7rem;font-weight:600;color:${accent};margin-bottom:4px">
                  ${isSendingNow ? html`<span class="inbox-sched-sending-dot"></span>` : iconSvg}
                  ${statusLabel}
                </div>
                <div style="font-size:0.84rem;color:rgba(241,244,249,0.7);word-break:break-word;line-height:1.4">${body.length > 120 ? body.slice(0, 120) + '…' : body}</div>
                ${actions}
              </div>
            `;
          })}
        </div>
      `}

      <!-- Composer -->
      <${Composer}
        onSend=${handleSend}
        onSchedule=${handleSchedule}
        prefill=${prefillSignal}
        replyTo=${replyTo}
        onCancelReply=${() => setReplyTo(null)}
        disabled=${composerOff}
        disabledReason=${composerWhy}
        channelPhoneId=${channelPhoneId}
        lastInboundTs=${chat?.lastInboundTs}
      />

      <!-- Contact Panel (slide-in) -->
      ${contactPanelOpen && html`
        <${ContactPanel}
          chatId=${chatId}
          phone=${phone}
          chat=${chat}
          onClose=${() => setContactPanelOpen(false)}
        />
      `}

      <!-- Interactive picker (#94) — opened via Composer attach → Interactive -->
      ${sheets.interactive && html`
        <${InteractivePicker} onClose=${() => $sheets.setKey('interactive', false)} />
      `}

      <!-- #162 — Team member picker sheet. Sources its roster from the
           CRM /crm/team endpoint via $team.roster. Owner appears first;
           then all CRM team members alphabetically. Picking stages the
           target into pendingTransfer; the TransferConfirmModal below
           collects the optional hand-off note and fires the actual
           assign on Confirm. -->
      ${pickerOpen && html`
        <${TeamMemberPicker}
          currentAgent=${chat?.agent ? resolveAgent(chat.agent) : null}
          onPick=${handleTransferPick}
          onRelease=${handleTransferRelease}
          onClose=${() => setPickerOpen(false)}
        />
      `}

      <!-- #167 — Transfer confirmation modal. Adds an optional note for
           the new agent. Empty note = transfer with no body; non-empty
           note = persisted as kind='transfer' lead-note + ephemeral
           divider in this tab. -->
      ${pendingTransfer && html`
        <${TransferConfirmModal}
          toAgent=${pendingTransfer.toAgent}
          fromAgent=${pendingTransfer.fromAgent}
          onConfirm=${handleTransferConfirm}
          onCancel=${() => setPendingTransfer(null)}
        />
      `}

      <!-- #167 — Internal notes sheet. Refreshes on open via the sheet's
           own useEffect. Stays usable when the 24h window is closed. -->
      ${sheets.notes && html`
        <${NotesSheet}
          contactId=${chatId}
          channelPhoneId=${channelPhoneId}
          contactName=${chat?.name || chat?.contactName || phone}
          onClose=${() => $sheets.setKey('notes', false)}
        />
      `}

      <!-- #162 — Done confirmation sheet. Only shown when the current
           chat is owned by another agent; otherwise handleDone fires
           instantly. Composer text is untouched. -->
      ${doneConfirm && html`
        <div
          class="inbox-sheet-overlay"
          style="display:block;z-index:300"
          onClick=${() => setDoneConfirm(null)}
        ></div>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Mark as done"
          style="position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:301;width:min(92vw,360px);background:var(--bg-elev-2,#151925);border:1px solid rgba(255,255,255,0.08);border-radius:16px;padding:20px 18px 14px;box-shadow:0 12px 40px rgba(0,0,0,0.55)"
        >
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
            <div style="width:36px;height:36px;border-radius:50%;background:rgba(37,211,102,0.14);display:flex;align-items:center;justify-content:center;color:#25D366;flex-shrink:0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h3 style="margin:0;font-size:1rem;font-weight:600;color:var(--text-primary)">Mark as done?</h3>
          </div>
          <p style="margin:0 0 16px;font-size:0.85rem;color:var(--text-secondary,#a4adc1);line-height:1.45">
            This chat is currently assigned to
            <b style="color:var(--text-primary)">${doneConfirm.currentAssignee?.label || 'another agent'}</b>.
            Marking it as done will release the chat so bots or any agent can handle the next message.
          </p>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button
              onClick=${() => setDoneConfirm(null)}
              style="padding:8px 16px;background:transparent;border:1px solid rgba(255,255,255,0.14);border-radius:10px;color:var(--text-secondary,#a4adc1);font-size:0.85rem;font-weight:500;cursor:pointer;font-family:inherit"
            >
              Cancel
            </button>
            <button
              onClick=${async () => { setDoneConfirm(null); await _fireDone(); }}
              style="padding:8px 16px;background:#25D366;border:none;border-radius:10px;color:#0a0c12;font-size:0.85rem;font-weight:600;cursor:pointer;font-family:inherit"
            >
              Mark done
            </button>
          </div>
        </div>
      `}

      <!-- #160 — transfer confirmation modal. Appears when the agent
           tries to send/react on a chat currently owned by a different
           teammate. Composer text stays intact while the modal is open;
           cancelling leaves it untouched. Confirming fires the assign
           API then replays the pending action. -->
      ${transferPrompt && html`
        <div
          class="inbox-sheet-overlay"
          style="display:block;z-index:300"
          onClick=${() => setTransferPrompt(null)}
        ></div>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Transfer chat"
          style="position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:301;width:min(92vw,360px);background:var(--bg-elev-2,#151925);border:1px solid rgba(255,255,255,0.08);border-radius:16px;padding:20px 18px 14px;box-shadow:0 12px 40px rgba(0,0,0,0.55)"
        >
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
            <div style="width:36px;height:36px;border-radius:50%;background:rgba(37,211,102,0.14);display:flex;align-items:center;justify-content:center;color:#25D366;flex-shrink:0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 3h5v5"/>
                <path d="M4 20 21 3"/>
                <path d="M21 16v5h-5"/>
                <path d="m15 15 6 6"/>
                <path d="M4 4l5 5"/>
              </svg>
            </div>
            <h3 style="margin:0;font-size:1rem;font-weight:600;color:var(--text-primary)">Transfer this chat?</h3>
          </div>
          <p style="margin:0 0 16px;font-size:0.85rem;color:var(--text-secondary,#a4adc1);line-height:1.45">
            This conversation is currently assigned to
            <b style="color:var(--text-primary)">${transferPrompt.currentAssignee?.label || 'another agent'}</b>.
            Transfer it to yourself and send your message?
          </p>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button
              onClick=${() => setTransferPrompt(null)}
              style="padding:8px 16px;background:transparent;border:1px solid rgba(255,255,255,0.14);border-radius:10px;color:var(--text-secondary,#a4adc1);font-size:0.85rem;font-weight:500;cursor:pointer;font-family:inherit"
            >
              Cancel
            </button>
            <button
              onClick=${() => transferPrompt.run()}
              style="padding:8px 16px;background:#25D366;border:none;border-radius:10px;color:#0a0c12;font-size:0.85rem;font-weight:600;cursor:pointer;font-family:inherit"
            >
              Transfer & send
            </button>
          </div>
        </div>
      `}
    </div>
  `;
}
